import React from 'react'
import { Box, Toolbar } from '@mui/material'
import { Navbar } from './Navbar'
import { RoleSidebar } from './RoleSidebar'
import { BaseComponentProps, UserRole } from '../../types/index'
import { useAuth } from '../../hooks/useAuth'
import ProviderAppBar from './ProviderAppBar'

interface MainLayoutProps extends BaseComponentProps {
  showSidebar?: boolean
}

/**
 * Main layout component that dynamically renders role-based sidebars
 * This is the primary layout wrapper for authenticated pages
 */
export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  showSidebar = true,
  className,
}) => {
  const { isAuthenticated, role } = useAuth()

  // Don't render layout for unauthenticated users
  if (!isAuthenticated) {
    return <>{children}</>
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        height: '100vh',
        overflow: 'hidden',
      }}
      className={className}
    >
      {/* Top Navigation Bar based on user role*/}
      {role === UserRole.THERAPIST && <ProviderAppBar />}
      {(role === UserRole.CLIENT ||
        role === UserRole.ADMIN ||
        role === UserRole.CERTIFICATE_MANAGER) && <Navbar />}

      {/* Main Content Container */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Role-based Sidebar */}
        {showSidebar && <RoleSidebar userRole={role} />}

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.default',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Toolbar spacer to account for fixed navbar */}
          <Toolbar sx={{ minHeight: '80px !important' }} />

          {/* Scrollable content area */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: { xs: 2, sm: 3 },
              py: 2,
              maxWidth: '100%',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default MainLayout
